import React, { FC } from 'react'
import { TechStackItem } from '@portfolio/models'
import { styled } from '@portfolio/styles'
import { Button, Media } from '@portfolio/components'
import { Close } from '@portfolio/icons'

export type TechStackModalProps = {
  closeModal: () => void
  techStack?: TechStackItem[]
}

const StyledModalContent = styled('div', {
  padding: '$16 $20 $24',
  backgroundColor: '$white',
  zIndex: '$content',

  '@md': {
    padding: '$32 $40 $40',
    borderRadius: '$r3',
    margin: '$72 0',
    maxWidth: '$techStackModalWidth',
  },
})

const StyledHeaderWrapper = styled('div', {
  position: 'sticky',
  top: '0',
  backgroundColor: '$white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '$8 0',
  marginBottom: '$32',
  zIndex: '$content',

  '@md': {
    marginBottom: '$48',
    position: 'unset',
  },
})

const StyledTitle = styled('h2', {
  projectFont: 'heading08',
})

const StyledButton = styled(Button, {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  width: '$40',
  height: '$40',
  borderRadius: '$rMax',
  background: '$black_005',
  transition: '0.2s background',

  '@md': {
    '&:hover': {
      background: '$black_01',
    },
  },

  '&:focus-visible': {
    outline: '$focusBlue solid 3px;',
  },

  '&:disabled': {
    display: 'none',
  },
})

const StyledTechStackWrapper = styled('div', {
  '& > div': {
    padding: '$12 0 $20',
    borderBottom: '1px solid $gray500_01',

    '@md': {
      padding: '$16 0 $24',
    },
  },

  '& > div:first-child': {
    borderTop: '1px solid $gray500_01',
  },
})

const StyledTechStack = styled('div', {
  display: 'grid',
  gap: '$12',

  '@md': {
    gap: '$4',
    gridTemplateColumns: '1.1fr 1fr',
  },
})

const StyledType = styled('p', {
  projectFont: 'ui03',
  textTransform: 'uppercase',
  marginBottom: '$16',

  '@md': {
    marginBottom: '$20',
  },
})

const StyledDescription = styled('p', {
  projectFont: 'body04',
  minHeight: '$techStackModalTextHeight',
})

const IconWrap = styled('div', {
  width: '80px',
})
const TechStackModal: FC<TechStackModalProps> = ({ closeModal, techStack }) => {
  return (
    <StyledModalContent>
      <StyledHeaderWrapper>
        <StyledTitle>Tech stack</StyledTitle>
        <StyledButton onClick={closeModal} aria-label="close" appearance="grid">
          <Close />
        </StyledButton>
      </StyledHeaderWrapper>

      <StyledTechStackWrapper>
        {techStack?.map((tech, index) => (
          <div key={`${index}-${tech.name}`}>
            <StyledType>{tech.type}</StyledType>
            <StyledTechStack>
              <IconWrap>
                <Media {...tech.logo} layout="responsive" sizes="80px" />
              </IconWrap>
              <StyledDescription>{tech.shortDescription}</StyledDescription>
            </StyledTechStack>
          </div>
        ))}
      </StyledTechStackWrapper>
    </StyledModalContent>
  )
}

export default TechStackModal
